// pages/WorkDetail.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import TransitionLink from '../components/TransitionLink';
import { useWorks } from '../context/workConfig';
import { useWorksLoading } from '../hooks/useWorksLoading';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import SEO from '../components/SEO';

const WorkDetail = () => {
    const { id } = useParams();
    const { getProjectById, getNextProject } = useWorks();
    const { language } = useLanguage();
    const t = useTranslation(language);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const contentRef = useRef(null);
    const galleryRef = useRef(null);
    const [nextProject, setNextProject] = useState(null);

    // Works loading hook for project-specific resources
    const {
        loadProject,
        isProjectLoaded,
        isProjectLoading,
        getProjectProgress
    } = useWorksLoading();

    useEffect(() => {
        setLoading(true);
        setActiveImageIndex(0); // Reset gallery index when switching projects
        const foundProject = getProjectById(id);

        if (foundProject) {
            setProject(foundProject);
            setNextProject(getNextProject(id));

            // Load this project's resources immediately
            loadProject(foundProject).then(() => {
                setLoading(false);
            });
        } else {
            setLoading(false);
        }

        const scrollToTop = () => {
            if (window.lenis) {
                window.lenis.scrollTo(0, { immediate: true });
            } else {
                window.scrollTo(0, 0);
            }
        };

        setTimeout(scrollToTop, 100);
    }, [id, getProjectById, getNextProject, loadProject]);

    // Handle scroll-triggered back-to-top button visibility
    useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 400);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const nextImage = () => {
        if (!project) return;
        setActiveImageIndex((prev) => (prev + 1) % project.gallery.length);
    };

    const prevImage = () => {
        if (!project) return;
        setActiveImageIndex((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
    };

    const handleBackToTop = () => {
        if (window.lenis) {
            window.lenis.scrollTo(0, {
                duration: 2.0,
                easing: (t) => 1 - Math.pow(1 - t, 5),
                offset: 0,
            });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    if (loading || (project && isProjectLoading(project.id))) {
        return (
            <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
                <div className="text-white text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        className="relative"
                    >
                        {/* Spinning loader */}
                        <div className="w-16 h-16 mx-auto mb-6 relative">
                            <div className="absolute inset-0 border-2 border-white/20 rounded-full"></div>
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="absolute inset-0 border-2 border-accent border-t-transparent rounded-full"
                            />
                        </div>

                        <div className="text-lg mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            {t('loading_project')}
                        </div>
                        <div className="text-sm text-gray-400">
                            {project ? `${getProjectProgress(project.id)}%` : t('preparing')}
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-4xl mb-6">{t('project_not_found')}</h1>
                        <TransitionLink to="/works" className="text-accent hover:underline">
                            {t('return_to_works')}
                        </TransitionLink>
                    </div>
                </div>
            </div>
        );
    }

    const projectTitle = t(project.title);
    const projectDescription = t(project.description);
    const projectImage = project.mainImage ? `https://paoloallegretti.com${project.mainImage}` : undefined;

    const workSchema = {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        "name": projectTitle,
        "description": projectDescription,
        "image": projectImage,
        "url": `https://paoloallegretti.com/work/${project.id}`,
        "creator": { "@type": "Person", "name": "Paolo Allegretti" },
        "dateCreated": project.year,
        "client": project.client,
        "genre": project.category
    };

    return (
        <div ref={contentRef} className="min-h-screen bg-black text-white">
            <SEO
                title={projectTitle}
                description={projectDescription}
                url={`/work/${project.id}`}
                image={projectImage}
                type="article"
                language={language}
                schema={workSchema}
            />
            <Header />

            {/* Breadcrumb Navigation */}
            <div className="relative pt-20 pb-4">
                <div className="container mx-auto px-4 md:px-8 lg:px-16">
                    <nav className="flex items-center space-x-2 text-sm text-white/60">
                        <TransitionLink to="/" className="hover:text-white transition-colors">
                            {t('home')}
                        </TransitionLink>
                        <span>/</span>
                        <TransitionLink to="/works" className="hover:text-white transition-colors">
                            {t('works')}
                        </TransitionLink>
                        <span>/</span>
                        <span className="text-white">{t(project.title)}</span>
                    </nav>
                </div>
            </div>

            <div className="relative pb-12">
                <div className="container mx-auto px-4 md:px-8 lg:px-16">
                    <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
                        <div className="md:w-1/2">
                            <div className="flex flex-wrap items-center gap-x-4 mb-4">
                                <span className="text-accent font-medium">{project.category}</span>
                                <span className="text-white/60">{project.year}</span>
                            </div>

                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                {t(project.title)}
                            </h1>

                            <p className="text-lg md:text-xl text-white/80 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                {t(project.description)}
                            </p>

                            <div className="flex flex-wrap gap-3">
                                {project.technologies.slice(0, 4).map((tech, index) => (
                                    <span key={index} className="px-3 py-1 text-sm bg-white/10 text-white/90 rounded-full">
                                        {tech}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="md:w-1/2 relative work-detail-hero-image">
                            {/* Loading overlay for main image */}
                            {!isProjectLoaded(project.id) && (
                                <div className="absolute inset-0 bg-gray-800 rounded-lg flex items-center justify-center z-10">
                                    <div className="text-center">
                                        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-2 mx-auto"></div>
                                        <div className="text-xs text-gray-400">{t('wd_loading_image')}</div>
                                    </div>
                                </div>
                            )}

                            <img
                                src={project.mainImage}
                                alt={project.title}
                                className={`w-full h-auto rounded-lg transition-opacity duration-500 ${isProjectLoaded(project.id) ? 'opacity-100' : 'opacity-0'
                                    }`}
                                onLoad={() => {
                                    // Image loaded successfully
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <section className="bg-white text-black py-20 rounded-3xl">
                <div className="container mx-auto px-4 md:px-8 lg:px-16">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
                        <div className="md:col-span-3">
                            <div className="sticky top-32 space-y-8">
                                <div>
                                    <h4 className="text-sm text-gray-500 uppercase mb-2">{t('wd_client')}</h4>
                                    <p className="text-lg font-medium">{project.client}</p>
                                </div>

                                <div>
                                    <h4 className="text-sm text-gray-500 uppercase mb-2">{t('wd_services')}</h4>
                                    <ul className="space-y-1">
                                        {project.services.map((service, index) => (
                                            <li key={index} className="text-gray-800">{t(service)}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="text-sm text-gray-500 uppercase mb-2">{t('wd_technologies')}</h4>
                                    <ul className="space-y-1">
                                        {project.technologies.map((tech, index) => (
                                            <li key={index} className="text-gray-800">{tech}</li>
                                        ))}
                                    </ul>
                                </div>

                                {project.colors && (
                                    <div>
                                        <h4 className="text-sm text-gray-500 uppercase mb-2">{t('wd_color_palette')}</h4>
                                        <div className="flex space-x-2 mt-3">
                                            {project.colors.map((color, index) => (
                                                <div
                                                    key={index}
                                                    className="w-10 h-10 rounded-full shadow-sm"
                                                    style={{ backgroundColor: color }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="md:col-span-9">
                            <div className="mb-20">
                                <h2 className="text-3xl font-bold mb-8 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                    {t('wd_overview')}
                                </h2>
                                <div className="prose prose-lg max-w-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                    {t(project.longDescription).split('\n\n').map((paragraph, idx) => (
                                        <p key={idx} className="text-gray-800 mb-6">
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </div>

                            {project.gallery && project.gallery.length > 0 && (
                                <div className="mb-20" ref={galleryRef}>
                                    <h2 className="text-3xl font-bold mb-8 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        {t('wd_project_gallery')}
                                    </h2>

                                    <div className="relative rounded-xl overflow-hidden bg-gray-100">
                                        {/* Loading overlay for gallery */}
                                        {!isProjectLoaded(project.id) && (
                                            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center z-10">
                                                <div className="text-center text-white">
                                                    <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin mb-3 mx-auto"></div>
                                                    <div className="text-sm">{t('wd_loading_gallery')}</div>
                                                </div>
                                            </div>
                                        )}

                                        <motion.div
                                            key={activeImageIndex}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: isProjectLoaded(project.id) ? 1 : 0.3 }}
                                            transition={{ duration: 0.4 }}
                                            className="aspect-[16/9]"
                                        >
                                            <img
                                                src={project.gallery[activeImageIndex].url}
                                                alt={project.gallery[activeImageIndex].caption}
                                                className="w-full h-full object-cover"
                                            />
                                        </motion.div>

                                        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/70 to-transparent">
                                            <p className="text-white text-base md:text-lg">
                                                {t(project.gallery[activeImageIndex].caption)}
                                            </p>
                                        </div>

                                        {/* Navigation arrows */}
                                        <button
                                            onClick={prevImage}
                                            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300"
                                            aria-label="Previous image"
                                        >
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>

                                        <button
                                            onClick={nextImage}
                                            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300"
                                            aria-label="Next image"
                                        >
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Gallery thumbnails */}
                                    <div className="mt-4 flex space-x-2 overflow-x-auto py-2">
                                        {project.gallery.map((item, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setActiveImageIndex(index)}
                                                className={`w-20 h-14 flex-shrink-0 rounded-md overflow-hidden relative ${activeImageIndex === index ? 'ring-2 ring-accent' : 'opacity-70'
                                                    }`}
                                            >
                                                {/* Thumbnail loading state */}
                                                {!isProjectLoaded(project.id) && (
                                                    <div className="absolute inset-0 bg-gray-600 animate-pulse"></div>
                                                )}
                                                <img
                                                    src={item.url}
                                                    alt={`Thumbnail ${index}`}
                                                    className={`w-full h-full object-cover transition-opacity duration-300 ${isProjectLoaded(project.id) ? 'opacity-100' : 'opacity-0'
                                                        }`}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {project.videoUrl && (
                                <div className="mb-20">
                                    <h2 className="text-3xl font-bold mb-8 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        {t('wd_project_video')}
                                    </h2>

                                    <div
                                        className="
        mx-auto 
        w-full 
        max-w-[min(100vw-2rem,1200px)]   /* oppure 1400px / 1600px a seconda del tuo design */
        rounded-lg 
        overflow-hidden 
        shadow-lg 
        bg-gray-900 
        relative
      "
                                    >
                                        {/* Overlay caricamento (lo lasciamo invariato) */}
                                        {!isProjectLoaded(project.id) && (
                                            <div className="absolute inset-0 bg-gray-800 flex items-center justify-center z-10">
                                                <div className="text-center text-white">
                                                    <div className="w-12 h-12 border-2 border-accent border-t-transparent rounded-full animate-spin mb-3 mx-auto"></div>
                                                    <div className="text-sm">{t('wd_loading_video')}</div>
                                                </div>
                                            </div>
                                        )}

                                        <video
                                            src={project.videoUrl}
                                            className={`
          w-full 
          h-auto 
          block 
          transition-opacity duration-500
          ${isProjectLoaded(project.id) ? 'opacity-100' : 'opacity-0'}
        `}
                                            controls
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            poster={project.mainImage}
                                            preload="metadata"           // aiuta a caricare solo i metadati → aspect ratio subito disponibile
                                        />
                                    </div>
                                </div>
                            )}

                            {project.testimonial && (
                                <div className="mb-10">
                                    <h2 className="text-3xl font-bold mb-8 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                        {t('wd_client_feedback')}
                                    </h2>

                                    <div className="bg-gray-50 p-6 md:p-8 rounded-xl border border-gray-100">
                                        <div className="flex items-start">
                                            <svg className="w-10 h-10 text-accent/30 mr-4 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M11.192 15.757c0-.88-.23-1.618-.69-2.217-.326-.412-.768-.683-1.327-.812-.55-.128-1.07-.137-1.54-.028-.16-.95.077-1.928.904-2.934.824-1.004 1.705-1.592 2.643-1.76.134-.232.24-.418.32-.553.326-.565.49-1.005.49-1.32 0-.317-.1-.573-.298-.77-.2-.196-.486-.294-.86-.294-.154 0-.313.015-.473.044l-.667.151.083-.656.088-.01c.053 0 .105-.005.157-.017 1.661-.252 2.969-.01 3.923.728.953.739 1.43 1.884 1.43 3.434v5.15c0 .2-.106.39-.316.57-.21.182-.468.324-.775.428-.55.18-1.113.27-1.688.27-.654 0-1.2-.087-1.639-.26-.439-.174-.78-.411-1.023-.713-.242-.303-.364-.635-.364-.997 0-.366.12-.701.36-1.003.241-.304.58-.142 1.02.163.44.304.696.456 1.24.456zm9.45 0c0-.88-.23-1.618-.69-2.217-.326-.42-.77-.695-1.327-.83-.55-.13-1.07-.138-1.54-.028-.16-.95.077-1.927.904-2.933.824-1.003 1.705-1.592 2.643-1.76.134-.232.24-.418.32-.553.326-.565.49-1.005.49-1.32 0-.317-.1-.573-.298-.77-.2-.196-.486-.294-.86-.294-.154 0-.312.015-.473.044l-.667.151.083-.656.088-.01c.053 0 .105-.005.157-.017 1.661-.252 2.969-.01 3.923.728.953.739 1.43 1.884 1.43 3.434v5.15c0 .2-.106.39-.316.57-.21.182-.468.324-.775.428-.55.18-1.113.27-1.688.27-.654 0-1.2-.087-1.639-.26-.439-.174-.78-.411-1.023-.713-.242-.303-.364-.635-.364-.997 0-.366.12-.701.36-1.003.241-.304.58-.142 1.02.163.44.304.696.456 1.24.456z" />
                                            </svg>

                                            <div>
                                                <blockquote className="text-xl italic text-gray-800 mb-4">
                                                    "{t(project.testimonial.quote)}"
                                                </blockquote>
                                                <div className="font-bold text-gray-900">{project.testimonial.author}</div>
                                                <div className="text-gray-600">{t(project.testimonial.role)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {nextProject && (
                <section className="bg-black text-white py-16">
                    <div className="container mx-auto px-4 md:px-8 lg:px-16">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                            <h2 className="text-2xl font-bold">{t('next_project')}</h2>
                            <TransitionLink to="/works" className="header-nav-link text-accent hover:underline mt-2 md:mt-0">
                                {t('view_all_works')}
                            </TransitionLink>
                        </div>

                        <TransitionLink
                            to={`/work/${nextProject.id}`}
                            className="block relative rounded-lg overflow-hidden group project-content-area"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-8">
                                <div className="md:col-span-2 aspect-video md:aspect-auto overflow-hidden rounded-lg">
                                    <div
                                        className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                                        style={{ backgroundImage: `url(${nextProject.mainImage})` }}
                                    />
                                </div>

                                <div className="md:col-span-3 py-2 md:py-6">
                                    <div className="text-accent mb-2">{nextProject.category}</div>
                                    <h3 className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-accent transition-colors duration-300">
                                        {t(nextProject.title)}
                                    </h3>
                                    <p className="text-white/70 mb-4 max-w-xl">
                                        {t(nextProject.description)}
                                    </p>

                                    <div className="flex items-center">
                                        <span className="text-white mr-2">{t('view_project')}</span>
                                        <svg className="w-5 h-5 text-accent transform transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </TransitionLink>
                    </div>
                </section>
            )}

            {/* Scroll-triggered back-to-top button */}
            <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                    opacity: showBackToTop ? 1 : 0,
                    scale: showBackToTop ? 1 : 0.8
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                onClick={handleBackToTop}
                className="fixed bottom-6 right-6 w-10 h-10 bg-accent/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-accent transition-colors duration-300 z-30 scroll-to-top-btn"
                style={{ pointerEvents: showBackToTop ? 'auto' : 'none' }}
                aria-label="Back to top"
            >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
            </motion.button>
        </div>
    );
};

export default WorkDetail;